"use client"

import { useState, useEffect } from "react"
import { X, Plus, Loader, Edit2, Trash2, Save, AlertCircle } from "lucide-react"
import { postAws } from "../postAws"

export const CourseQuizEditModal = ({ isOpen, onClose, courseId, topicId, quizData, updateCourse, showToast }) => {
  const [editedQuiz, setEditedQuiz] = useState({
    id: "",
    title: "",
    description: "",
    questions: [],
    totalMarks: 0,
  })
  const [isUpdating, setIsUpdating] = useState(false)
  const [editingQuestionId, setEditingQuestionId] = useState(null)
  const [questionToEdit, setQuestionToEdit] = useState(null)

  // Initialize the quiz data when the modal opens
  useEffect(() => {
    if (isOpen && quizData) {
      let parsedQuestions = []

      try {
        // Parse quiz data if it's a string
        if (typeof quizData.quizData === "string") {
          parsedQuestions = JSON.parse(quizData.quizData)
        } else if (Array.isArray(quizData.quizData)) {
          parsedQuestions = quizData.quizData
        }

        // Convert the server format to our internal format
        const formattedQuestions = parsedQuestions.map((q) => {
          // Create a unique ID for each question
          const questionId = q.id || Date.now() + Math.floor(Math.random() * 1000)

          if (q.questionType === "multiple-choice" || q.questionType === "true-false") {
            // Format options as objects with id, text, and isCorrect properties
            const options = (q.options || []).map((opt, index) => {
              const optionText = typeof opt === "string" ? opt : opt.text
              return {
                id: Date.now() + index + Math.floor(Math.random() * 1000),
                text: optionText,
                isCorrect:
                  q.correctAnswer === index || (typeof q.correctAnswer === "string" && optionText === q.correctAnswer),
              }
            })

            return {
              id: questionId,
              text: q.question || q.text || "",
              options: options,
              type: q.questionType || "multiple-choice",
              explanation: q.explanation || "",
              marks: q.marks || 1,
            }
          } else if (q.questionType === "short-answer") {
            // For short answer questions, create a single "correct" option
            return {
              id: questionId,
              text: q.question || q.text || "",
              options: [
                {
                  id: Date.now() + Math.floor(Math.random() * 1000),
                  text: q.correctAnswer || "",
                  isCorrect: true,
                },
              ],
              type: "short-answer",
              explanation: q.explanation || "",
              marks: q.marks || 1,
            }
          }

          // Default case - create a standard multiple choice question
          return {
            id: questionId,
            text: q.question || q.text || "",
            options: [
              { id: Date.now() + 1, text: "", isCorrect: false },
              { id: Date.now() + 2, text: "", isCorrect: false },
              { id: Date.now() + 3, text: "", isCorrect: false },
              { id: Date.now() + 4, text: "", isCorrect: false },
            ],
            type: "multiple-choice",
            explanation: q.explanation || "",
            marks: q.marks || 1,
          }
        })

        setEditedQuiz({
          id: quizData.id,
          title: quizData.title || "",
          description: quizData.description || "",
          questions: formattedQuestions,
          totalMarks: quizData.totalMarks || formattedQuestions.reduce((sum, q) => sum + (q.marks || 1), 0),
        })
      } catch (error) {
        console.error("Error parsing quiz data:", error)
        // Set default empty quiz if parsing fails
        setEditedQuiz({
          id: quizData.id,
          title: quizData.title || "",
          description: quizData.description || "",
          questions: [],
          totalMarks: quizData.totalMarks || 0,
        })
      }
    }
  }, [isOpen, quizData])

  if (!isOpen) return null

  const handleQuestionEdit = (questionId) => {
    const question = editedQuiz.questions.find((q) => q.id === questionId)
    if (question) {
      // For true/false questions, ensure we have exactly two options: True and False
      if (question.type === "true-false") {
        const trueOption = question.options.find((o) => o.text === "True") || {
          id: Date.now(),
          text: "True",
          isCorrect: false,
        }
        const falseOption = question.options.find((o) => o.text === "False") || {
          id: Date.now() + 1,
          text: "False",
          isCorrect: false,
        }

        // Make sure one of them is marked as correct
        if (!trueOption.isCorrect && !falseOption.isCorrect) {
          trueOption.isCorrect = true
        }

        setQuestionToEdit({
          ...question,
          options: [trueOption, falseOption],
        })
      }
      // For short answer questions, ensure we have at least one option for the correct answer
      else if (question.type === "short-answer") {
        const correctOption = question.options.find((o) => o.isCorrect) || { id: Date.now(), text: "", isCorrect: true }

        setQuestionToEdit({
          ...question,
          options: [correctOption],
        })
      }
      // For multiple choice, use the existing options
      else {
        setQuestionToEdit({ ...question })
      }

      setEditingQuestionId(questionId)
    }
  }

  const handleQuestionDelete = (questionId) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      const updatedQuestions = editedQuiz.questions.filter((q) => q.id !== questionId)
      setEditedQuiz({
        ...editedQuiz,
        questions: updatedQuestions,
        totalMarks: updatedQuestions.length,
      })
    }
  }

  const handleAddQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      text: "",
      type: "multiple-choice",
      options: [
        { id: Date.now() + 1, text: "", isCorrect: false },
        { id: Date.now() + 2, text: "", isCorrect: false },
        { id: Date.now() + 3, text: "", isCorrect: false },
        { id: Date.now() + 4, text: "", isCorrect: false },
      ],
      explanation: "",
      marks: 1,
    }

    setQuestionToEdit(newQuestion)
    setEditingQuestionId("new")
  }

  const handleQuestionChange = (field, value) => {
    setQuestionToEdit({
      ...questionToEdit,
      [field]: value,
    })
  }

  const handleOptionChange = (optionId, field, value) => {
    setQuestionToEdit({
      ...questionToEdit,
      options: questionToEdit.options.map((opt) => {
        if (opt.id === optionId) {
          if (field === "isCorrect" && value === true) {
            // If setting this option as correct, make all other options incorrect
            return { ...opt, isCorrect: true }
          }
          return { ...opt, [field]: value }
        } else if (field === "isCorrect" && value === true) {
          // If setting another option as correct, make this one incorrect
          return { ...opt, isCorrect: false }
        }
        return opt
      }),
    })
  }

  const handleAddOption = () => {
    if (questionToEdit.options.length >= 6) {
      showToast("Maximum 6 options allowed per question", "error")
      return
    }

    setQuestionToEdit({
      ...questionToEdit,
      options: [...questionToEdit.options, { id: Date.now(), text: "", isCorrect: false }],
    })
  }

  const handleDeleteOption = (optionId) => {
    if (questionToEdit.options.length <= 2) {
      showToast("Question must have at least 2 options", "error")
      return
    }

    setQuestionToEdit({
      ...questionToEdit,
      options: questionToEdit.options.filter((opt) => opt.id !== optionId),
    })
  }

  const saveQuestion = () => {
    // Validate question
    if (!questionToEdit.text.trim()) {
      showToast("Question text is required", "error")
      return
    }

    // Validate based on question type
    if (questionToEdit.type === "multiple-choice") {
      // Check if at least one option is marked as correct
      if (!questionToEdit.options.some((opt) => opt.isCorrect)) {
        showToast("At least one option must be marked as correct", "error")
        return
      }

      // Check if all options have text
      if (questionToEdit.options.some((opt) => !opt.text.trim())) {
        showToast("All options must have text", "error")
        return
      }
    } else if (questionToEdit.type === "short-answer") {
      // Check if the correct answer is provided
      if (!questionToEdit.options[0] || !questionToEdit.options[0].text.trim()) {
        showToast("Correct answer is required", "error")
        return
      }
    } else if (questionToEdit.type === "true-false") {
      // Ensure we have True and False options and one is marked as correct
      const hasTrue = questionToEdit.options.some((opt) => opt.text === "True")
      const hasFalse = questionToEdit.options.some((opt) => opt.text === "False")
      const hasCorrect = questionToEdit.options.some((opt) => opt.isCorrect)

      if (!hasTrue || !hasFalse || !hasCorrect) {
        showToast("True/False question must have both options with one marked as correct", "error")
        return
      }
    }

    if (editingQuestionId === "new") {
      // Add new question
      setEditedQuiz({
        ...editedQuiz,
        questions: [...editedQuiz.questions, questionToEdit],
        totalMarks: editedQuiz.questions.reduce((sum, q) => sum + (q.marks || 1), 0) + (questionToEdit.marks || 1),
      })
    } else {
      // Update existing question
      setEditedQuiz({
        ...editedQuiz,
        questions: editedQuiz.questions.map((q) => (q.id === editingQuestionId ? questionToEdit : q)),
        totalMarks: editedQuiz.questions.reduce(
          (sum, q) => sum + (q.id === editingQuestionId ? questionToEdit.marks || 1 : q.marks || 1),
          0,
        ),
      })
    }

    // Reset editing state
    setEditingQuestionId(null)
    setQuestionToEdit(null)
  }

  const cancelQuestionEdit = () => {
    setEditingQuestionId(null)
    setQuestionToEdit(null)
  }

  const handleUpdateQuiz = async () => {
    // Validate quiz data
    if (!editedQuiz.title.trim()) {
      showToast("Quiz title is required", "error")
      return
    }

    if (editedQuiz.questions.length === 0) {
      showToast("Quiz must have at least one question", "error")
      return
    }

    setIsUpdating(true)

    try {
      // Format questions for the server
      const formattedQuestions = editedQuiz.questions.map((q) => {
        // Determine the correct answer as index
        const correctIndex = q.options.findIndex((opt) => opt.isCorrect)

        // Format based on question type
        if (q.type === "short-answer") {
          return {
            questionType: "short-answer",
            question: q.text,
            options: [],
            correctAnswer: q.options[0]?.text || "",
            explanation: q.explanation || "",
            marks: q.marks || 1,
          }
        } else if (q.type === "true-false") {
          return {
            questionType: "true-false",
            question: q.text,
            options: q.options.map((opt) => opt.text),
            correctAnswer: correctIndex,
            explanation: q.explanation || "",
            marks: q.marks || 1,
          }
        } else {
          // Default to multiple-choice
          return {
            questionType: "multiple-choice",
            question: q.text,
            options: q.options.map((opt) => opt.text),
            correctAnswer: correctIndex,
            explanation: q.explanation || "",
            marks: q.marks || 1,
          }
        }
      })

      // Calculate total marks
      const totalMarks = formattedQuestions.reduce((sum, q) => sum + (q.marks || 1), 0)

      // Prepare the updates object for the quiz
      const updates = {
        title: editedQuiz.title,
        description: editedQuiz.description,
        quiz: {
          data: JSON.stringify(formattedQuestions),
          totalMarks: totalMarks,
        },
      }

      // Call the update-subtopic API
      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
        request: "edit-subtopic",
        body: {
          courseId: courseId,
          topicId: topicId,
          subtopicId: editedQuiz.id,
          updates: updates,
        },
      })

      if (response.statusCode === 200) {
        // Update the course state with the updated quiz
        updateCourse((prev) => ({
          ...prev,
          topics: prev.topics.map((topic) => {
            if (topic.id === topicId) {
              return {
                ...topic,
                content: topic.content.map((item) => {
                  if (item.id === editedQuiz.id) {
                    return {
                      ...item,
                      title: editedQuiz.title,
                      description: editedQuiz.description,
                      quizData: formattedQuestions,
                      totalMarks: totalMarks,
                    }
                  }
                  return item
                }),
              }
            }
            return topic
          }),
        }))

        showToast("Quiz updated successfully")
        onClose()
      } else {
        throw new Error(response.body?.message || "Failed to update quiz")
      }
    } catch (error) {
      console.error("Error updating quiz:", error)
      showToast("Error updating quiz. Please try again.", "error")
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Edit Quiz</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Quiz Title and Description */}
        <div className="space-y-4 mb-6">
          <div>
            <label htmlFor="quiz-title" className="block text-sm font-medium text-gray-700 mb-1">
              Quiz Title
            </label>
            <input
              id="quiz-title"
              type="text"
              value={editedQuiz.title}
              onChange={(e) => setEditedQuiz({ ...editedQuiz, title: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder="Enter quiz title"
              required
            />
          </div>

          <div>
            <label htmlFor="quiz-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              id="quiz-description"
              value={editedQuiz.description}
              onChange={(e) => setEditedQuiz({ ...editedQuiz, description: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[100px]"
              placeholder="Enter quiz description"
            />
          </div>
        </div>

        {/* Question Editor */}
        {editingQuestionId && questionToEdit && (
          <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-200">
            <h3 className="text-lg font-medium text-blue-800 mb-4">
              {editingQuestionId === "new" ? "Add New Question" : "Edit Question"}
            </h3>

            <div className="space-y-4">
              <div>
                <label htmlFor="question-text" className="block text-sm font-medium text-gray-700 mb-1">
                  Question Text
                </label>
                <input
                  id="question-text"
                  type="text"
                  value={questionToEdit.text}
                  onChange={(e) => handleQuestionChange("text", e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Enter question text"
                  required
                />
              </div>

              <div className="mt-4">
                <label htmlFor="question-type" className="block text-sm font-medium text-gray-700 mb-1">
                  Question Type
                </label>
                <select
                  id="question-type"
                  value={questionToEdit.type || "multiple-choice"}
                  onChange={(e) => handleQuestionChange("type", e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                  <option value="multiple-choice">Multiple Choice</option>
                  <option value="true-false">True/False</option>
                  <option value="short-answer">Short Answer</option>
                </select>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {questionToEdit.type === "short-answer" ? "Correct Answer" : "Options"}
                  </label>
                  {questionToEdit.type !== "short-answer" && questionToEdit.type !== "true-false" && (
                    <button
                      type="button"
                      onClick={handleAddOption}
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Option
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  {questionToEdit.type === "short-answer" ? (
                    // Short answer has just one text field for the correct answer
                    <div className="flex items-start space-x-3">
                      <textarea
                        value={questionToEdit.options[0]?.text || ""}
                        onChange={(e) => handleOptionChange(questionToEdit.options[0]?.id, "text", e.target.value)}
                        className="flex-1 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[80px]"
                        placeholder="Enter the correct answer"
                        required
                      />
                    </div>
                  ) : questionToEdit.type === "true-false" ? (
                    // True/False has just two fixed options
                    <>
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name={`question-${questionToEdit.id}-correct`}
                          checked={questionToEdit.options[0]?.isCorrect || false}
                          onChange={() => {
                            const updatedOptions = [
                              { id: questionToEdit.options[0]?.id || Date.now(), text: "True", isCorrect: true },
                              { id: questionToEdit.options[1]?.id || Date.now() + 1, text: "False", isCorrect: false },
                            ]
                            setQuestionToEdit({ ...questionToEdit, options: updatedOptions })
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span>True</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name={`question-${questionToEdit.id}-correct`}
                          checked={questionToEdit.options[1]?.isCorrect || false}
                          onChange={() => {
                            const updatedOptions = [
                              { id: questionToEdit.options[0]?.id || Date.now(), text: "True", isCorrect: false },
                              { id: questionToEdit.options[1]?.id || Date.now() + 1, text: "False", isCorrect: true },
                            ]
                            setQuestionToEdit({ ...questionToEdit, options: updatedOptions })
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span>False</span>
                      </div>
                    </>
                  ) : (
                    // Multiple choice options
                    questionToEdit.options.map((option, optionIndex) => (
                      <div key={option.id} className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name={`question-${questionToEdit.id}-correct`}
                          checked={option.isCorrect}
                          onChange={() => handleOptionChange(option.id, "isCorrect", true)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <input
                          type="text"
                          value={option.text}
                          onChange={(e) => handleOptionChange(option.id, "text", e.target.value)}
                          className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder={`Option ${optionIndex + 1}`}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => handleDeleteOption(option.id)}
                          className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="question-explanation" className="block text-sm font-medium text-gray-700 mb-1">
                  Explanation (Optional)
                </label>
                <textarea
                  id="question-explanation"
                  value={questionToEdit.explanation || ""}
                  onChange={(e) => handleQuestionChange("explanation", e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[80px]"
                  placeholder="Provide an explanation for the correct answer"
                />
              </div>

              <div className="mt-4">
                <label htmlFor="question-marks" className="block text-sm font-medium text-gray-700 mb-1">
                  Marks
                </label>
                <input
                  id="question-marks"
                  type="number"
                  min="1"
                  value={questionToEdit.marks || 1}
                  onChange={(e) => handleQuestionChange("marks", Number.parseInt(e.target.value) || 1)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={cancelQuestionEdit}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveQuestion}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Save Question
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Questions List */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Questions</h3>
            <button
              type="button"
              onClick={handleAddQuestion}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center text-sm"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Question
            </button>
          </div>

          {editedQuiz.questions.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
              <AlertCircle className="h-10 w-10 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">No questions added yet</p>
              <button
                type="button"
                onClick={handleAddQuestion}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors inline-flex items-center text-sm"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Your First Question
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {editedQuiz.questions.map((question, questionIndex) => (
                <div key={question.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  {editingQuestionId === question.id ? (
                    // Inline question editor
                    <div className="space-y-4">
                      <div>
                        <label
                          htmlFor={`question-text-${question.id}`}
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Question Text
                        </label>
                        <input
                          id={`question-text-${question.id}`}
                          type="text"
                          value={questionToEdit.text}
                          onChange={(e) => handleQuestionChange("text", e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          placeholder="Enter question text"
                          required
                        />
                      </div>

                      <div>
                        <label
                          htmlFor={`question-type-${question.id}`}
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Question Type
                        </label>
                        <select
                          id={`question-type-${question.id}`}
                          value={questionToEdit.type || "multiple-choice"}
                          onChange={(e) => handleQuestionChange("type", e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        >
                          <option value="multiple-choice">Multiple Choice</option>
                          <option value="true-false">True/False</option>
                          <option value="short-answer">Short Answer</option>
                        </select>
                      </div>

                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {questionToEdit.type === "short-answer" ? "Correct Answer" : "Options"}
                          </label>
                          {questionToEdit.type !== "short-answer" && questionToEdit.type !== "true-false" && (
                            <button
                              type="button"
                              onClick={handleAddOption}
                              className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Add Option
                            </button>
                          )}
                        </div>

                        <div className="space-y-3">
                          {questionToEdit.type === "short-answer" ? (
                            // Short answer has just one text field for the correct answer
                            <div className="flex items-start space-x-3">
                              <textarea
                                value={questionToEdit.options[0]?.text || ""}
                                onChange={(e) =>
                                  handleOptionChange(questionToEdit.options[0]?.id, "text", e.target.value)
                                }
                                className="flex-1 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[80px]"
                                placeholder="Enter the correct answer"
                                required
                              />
                            </div>
                          ) : questionToEdit.type === "true-false" ? (
                            // True/False has just two fixed options
                            <>
                              <div className="flex items-center space-x-3">
                                <input
                                  type="radio"
                                  name={`question-${questionToEdit.id}-correct`}
                                  checked={questionToEdit.options[0]?.isCorrect || false}
                                  onChange={() => {
                                    const updatedOptions = [
                                      {
                                        id: questionToEdit.options[0]?.id || Date.now(),
                                        text: "True",
                                        isCorrect: true,
                                      },
                                      {
                                        id: questionToEdit.options[1]?.id || Date.now() + 1,
                                        text: "False",
                                        isCorrect: false,
                                      },
                                    ]
                                    setQuestionToEdit({ ...questionToEdit, options: updatedOptions })
                                  }}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                />
                                <span>True</span>
                              </div>
                              <div className="flex items-center space-x-3">
                                <input
                                  type="radio"
                                  name={`question-${questionToEdit.id}-correct`}
                                  checked={questionToEdit.options[1]?.isCorrect || false}
                                  onChange={() => {
                                    const updatedOptions = [
                                      {
                                        id: questionToEdit.options[0]?.id || Date.now(),
                                        text: "True",
                                        isCorrect: false,
                                      },
                                      {
                                        id: questionToEdit.options[1]?.id || Date.now() + 1,
                                        text: "False",
                                        isCorrect: true,
                                      },
                                    ]
                                    setQuestionToEdit({ ...questionToEdit, options: updatedOptions })
                                  }}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                />
                                <span>False</span>
                              </div>
                            </>
                          ) : (
                            // Multiple choice options
                            questionToEdit.options.map((option, optionIndex) => (
                              <div key={option.id} className="flex items-center space-x-3">
                                <input
                                  type="radio"
                                  name={`question-${questionToEdit.id}-correct`}
                                  checked={option.isCorrect}
                                  onChange={() => handleOptionChange(option.id, "isCorrect", true)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                />
                                <input
                                  type="text"
                                  value={option.text}
                                  onChange={(e) => handleOptionChange(option.id, "text", e.target.value)}
                                  className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                  placeholder={`Option ${optionIndex + 1}`}
                                  required
                                />
                                <button
                                  type="button"
                                  onClick={() => handleDeleteOption(option.id)}
                                  className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor={`question-explanation-${question.id}`}
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Explanation (Optional)
                        </label>
                        <textarea
                          id={`question-explanation-${question.id}`}
                          value={questionToEdit.explanation || ""}
                          onChange={(e) => handleQuestionChange("explanation", e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[80px]"
                          placeholder="Provide an explanation for the correct answer"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor={`question-marks-${question.id}`}
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Marks
                        </label>
                        <input
                          id={`question-marks-${question.id}`}
                          type="number"
                          min="1"
                          value={questionToEdit.marks || 1}
                          onChange={(e) => handleQuestionChange("marks", Number.parseInt(e.target.value) || 1)}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        />
                      </div>

                      <div className="flex justify-end space-x-3 pt-4">
                        <button
                          type="button"
                          onClick={cancelQuestionEdit}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={saveQuestion}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Save Question
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Question display view
                    <>
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="text-md font-medium text-gray-800">
                          Question {questionIndex + 1}: {question.text}
                        </h4>
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            onClick={() => handleQuestionEdit(question.id)}
                            className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                            disabled={editingQuestionId !== null && editingQuestionId !== question.id}
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleQuestionDelete(question.id)}
                            className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                            disabled={editingQuestionId !== null}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <div className="ml-4 space-y-1">
                        {question.options.map((option, optionIndex) => (
                          <div key={option.id} className="flex items-center space-x-2">
                            <div
                              className={`w-4 h-4 rounded-full ${option.isCorrect ? "bg-green-500" : "border border-gray-300"}`}
                            >
                              {option.isCorrect && <div className="w-2 h-2 bg-white rounded-full m-auto mt-1"></div>}
                            </div>
                            <span className={option.isCorrect ? "font-medium" : ""}>
                              {String.fromCharCode(65 + optionIndex)}. {option.text}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpdateQuiz}
            disabled={isUpdating || editingQuestionId !== null}
            className={`px-4 py-2 rounded-md ${
              isUpdating || editingQuestionId !== null
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            } text-white transition-colors`}
          >
            {isUpdating ? (
              <>
                <Loader className="inline-block h-4 w-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Save className="inline-block h-4 w-4 mr-2" />
                Save Quiz
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
